import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetInvoiceAssociations,
  useDeleteRebateInvoice,
} from '@/api/rebates/useDeleteRebateInvoice';
import { InvoiceAssociationInfo } from '@/api/rebates/types';
import { useToast } from 'src/context/ToastContext';

interface UseInvoiceDeleteModalProps {
  agreementId: string;
  onSuccess?: () => void;
}

export function useInvoiceDeleteModal({
  agreementId,
  onSuccess,
}: UseInvoiceDeleteModalProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceAssociationInfo | null>(
    null,
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [forceDeleteConfirmText, setForceDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: fetchAssociations } = useGetInvoiceAssociations();
  const { mutateAsync: deleteInvoice } = useDeleteRebateInvoice();

  // Step 1: Open modal and fetch association info
  const initiateDelete = async (invoiceId: string) => {
    try {
      const result = await fetchAssociations(invoiceId);
      setInvoiceInfo(result.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to load invoice details:', error);
      showToast({
        message: 'Failed to load invoice details',
        color: 'error',
      });
    }
  };

  // Step 2: Execute delete
  const confirmDelete = async (force = false) => {
    if (!invoiceInfo) return;

    setIsDeleting(true);
    try {
      const result = await deleteInvoice({
        invoiceId: invoiceInfo.invoiceId,
        agreementId,
        force,
      });

      // Success messages based on mode
      let message = '';
      if (result.data.mode === 'remove_association') {
        message = 'Invoice removed from this agreement';
      } else if (result.data.mode === 'cascade_delete') {
        message = 'Invoice deleted permanently';
      } else if (result.data.mode === 'force_delete') {
        message = `Invoice deleted from ${result.data.deleted.associations} agreement(s)`;
      }

      showToast({
        message,
        color: 'success',
      });

      // Invalidate forecast query cache to force regeneration
      // This ensures the forecast recalculates with the updated data
      await queryClient.invalidateQueries({
        queryKey: [`rebates/forecast/agreements/${agreementId}`],
      });

      // Cleanup and refresh
      closeModal();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      showToast({
        message:
          error instanceof Error ? error.message : 'Failed to delete invoice',
        color: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setInvoiceInfo(null);
    setShowAdvanced(false);
    setForceDeleteConfirmText('');
    setIsDeleting(false);
  };

  return {
    modalOpen,
    invoiceInfo,
    isDeleting,
    showAdvanced,
    forceDeleteConfirmText,
    setShowAdvanced,
    setForceDeleteConfirmText,
    initiateDelete,
    confirmDelete,
    closeModal,
  };
}
